import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal } from "./Reveal";
import Seo from "./Seo";

export default function LocalPage({ page }) {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "KLMProd",
      description: page.description,
      url: `https://klmprod.com${page.path}`,
      email: "contact@klmprod.com",
      priceRange: "800 € - 1600 €",
      areaServed: page.zones.map((z) => ({ "@type": "City", name: z })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: page.faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-[#080C14]" data-testid={`local-page-${page.slug}`}>
      <Seo title={page.title} description={page.description} path={page.path} jsonLd={jsonLd} />

      <header className="sticky top-0 z-40 border-b border-cyan-500/15 bg-[#080C14]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="local-back-link"
            className="group flex items-center gap-2 text-sm font-medium text-slate-300 transition-colors hover:text-cyan-300"
          >
            <ArrowLeft size={16} className="transition-transform duration-300 group-hover:-translate-x-1" />
            Retour au site
          </Link>
          <img src="/logo-v2.png" alt="KLMProd" className="h-8 w-auto" />
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={page.img} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#080C14]/60 via-[#080C14]/70 to-[#080C14]" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8">
          <Reveal>
            <p className="font-mono-brand text-xs uppercase tracking-[0.3em] text-cyan-400" data-testid="local-eyebrow">
              {page.eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="font-display mt-5 max-w-3xl text-4xl font-extrabold leading-tight tracking-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="local-h1">
              {page.h1} <span className="text-gradient">{page.h1Accent}</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{page.intro}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/#devis"
                data-testid="local-cta-devis"
                className="bg-brand-gradient group flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.3)] transition-transform duration-300 hover:scale-105"
              >
                {page.cta}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/tarifs"
                data-testid="local-cta-tarifs"
                className="rounded-full border border-cyan-400/30 px-7 py-3.5 text-sm font-semibold text-cyan-300 transition-colors duration-300 hover:border-cyan-400/60 hover:bg-cyan-400/10"
              >
                Voir les tarifs
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8" data-testid="local-zones">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-cyan-400">Zones desservies</p>
          <div className="mt-5 flex flex-wrap gap-2.5">
            {page.zones.map((z) => (
              <span
                key={z}
                className="flex items-center gap-1.5 rounded-full border border-cyan-500/20 bg-[#0E1524]/70 px-4 py-2 text-sm text-slate-300"
              >
                <MapPin size={13} className="text-cyan-400" />
                {z}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-6 lg:px-8">
        {page.sections.map((s, i) => (
          <Reveal key={s.title} delay={i * 0.08}>
            <div className="grid grid-cols-1 gap-3 border-t border-cyan-500/10 py-10 md:grid-cols-12" data-testid={`local-section-${i + 1}`}>
              <span className="font-mono-brand text-sm text-cyan-400 md:col-span-2">/0{i + 1}</span>
              <h2 className="font-display text-2xl font-bold text-slate-100 md:col-span-4">{s.title}</h2>
              <p className="max-w-xl text-base leading-relaxed text-slate-400 md:col-span-6">{s.text}</p>
            </div>
          </Reveal>
        ))}
        <div className="border-t border-cyan-500/10" />
      </section>

      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6" data-testid="local-faq">
        <Reveal>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-slate-50">
            Questions <span className="text-gradient">fréquentes.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="mt-8">
            {page.faq.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`q-${i}`}
                className="border-cyan-500/15"
                data-testid={`local-faq-${i + 1}`}
              >
                <AccordionTrigger className="text-left text-sm font-semibold text-slate-100 hover:text-cyan-300 sm:text-base">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal>
          <div className="rounded-[2rem] border border-cyan-400/20 bg-gradient-to-br from-[#0E1524] via-[#0B1E33] to-[#0E1524] p-10 text-center sm:p-14">
            <h2 className="font-display text-2xl font-extrabold text-slate-50 sm:text-3xl">
              Votre date se réserve <span className="text-gradient">maintenant.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              Devis gratuit et sans engagement, réponse sous 48 h. Acompte de 30 % à la signature,
              payable en ligne de façon sécurisée.
            </p>
            <Link
              to="/#devis"
              data-testid="local-cta-final"
              className="bg-brand-gradient group mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold text-[#04121f] shadow-[0_0_40px_rgba(0,150,255,0.3)] transition-transform duration-300 hover:scale-105"
            >
              {page.cta}
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
